import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Icons from "lucide-react";
import { STATS, SERVICES, WHY_US, TESTIMONIALS, HERO_IMAGES, CERT_LOGOS, IMAGES, COMPANY, REVIEWS_SUMMARY } from "../data/site";
import { PackageCard } from "../components/PackageCard";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { StatCounter, CTABand } from "../components/sections";
import { EnquiryForm } from "../components/EnquiryForm";
import { usePackages } from "../hooks/usePackages";
import { useBlogs } from "../hooks/useBlogs";
import { ParallaxImage } from "../components/Parallax";
import { HScroll } from "../components/HScroll";

const Icon = ({ name, ...p }) => {
  const C = Icons[name] || Icons.Sparkles;
  return <C {...p} />;
};

const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 140]);
  const bgScale = useTransform(scrollY, [0, 700], [1, 1.12]);
  const contentY = useTransform(scrollY, [0, 500], [0, -80]);
  const contentOpacity = useTransform(scrollY, [0, 380], [1, 0]);
  const navigate = useNavigate();
  const { data: packages = [] } = usePackages();

  const [idx, setIdx] = useState(0);
  const [q, setQ] = useState("");
  const [openSug, setOpenSug] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_IMAGES.length), 3000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenSug(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const suggestions = useMemo(() => {
    if (!q.trim()) return [];
    const s = q.toLowerCase();
    return packages
      .filter((p) => `${p.title} ${p.subtitle} ${p.region} ${p.summary}`.toLowerCase().includes(s))
      .slice(0, 5);
  }, [q, packages]);

  const runSearch = () => {
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <section data-testid="hero-section" className="relative h-screen min-h-[700px] flex items-center overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        {HERO_IMAGES.map((src, i) => (
          <img key={src} src={src} alt="Destination" className={`absolute inset-0 w-full h-[118%] object-cover transition-opacity duration-[1200ms] ${i === idx ? "opacity-100 kenburns-active" : "opacity-0"}`} />
        ))}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A221C]/70 via-[#0A221C]/30 to-[#0A221C]/80" />

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="ll-container relative w-full">
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="eyebrow text-[#4ef72d]">
          {COMPANY.tagline}
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.12 }} className="font-display text-4xl sm:text-6xl lg:text-8xl font-bold text-white mt-4 max-w-4xl leading-[1.03]">
          Journeys crafted <span className="italic text-[#4ef72d]">since 1991</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="text-white/90 text-base sm:text-lg md:text-xl mt-5 md:mt-6 max-w-xl">
          A trusted family-run travel house serving 3000+ travellers every year — across India and around the world.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.45 }} className="flex flex-wrap gap-4 mt-9">
          <Link to="/domestic-tours" data-testid="hero-cta-button" className="btn-primary">Explore Packages <Icons.ArrowRight size={18} /></Link>
          <Link to="/contact" className="btn-outline-light">Plan My Trip</Link>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div style={{ opacity: contentOpacity }} className="absolute bottom-40 md:bottom-36 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/70">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <Icons.ChevronDown size={22} />
        </motion.div>
      </motion.div>

      {/* Search bar */}
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }} className="absolute -bottom-px left-0 right-0">
        <div className="ll-container">
          <div ref={boxRef} data-testid="hero-enquiry-bar" className="relative bg-white rounded-t-3xl shadow-2xl p-6 md:p-8 grid md:grid-cols-4 gap-5 items-end translate-y-px">
            <div className="relative">
              <label className="eyebrow text-[#4A5D58] block mb-2">Search Destinations</label>
              <input
                data-testid="hero-search-destination"
                className="ll-input"
                placeholder="Try 'Bhutan', 'Andaman', 'Kailash'..."
                value={q}
                onChange={(e) => { setQ(e.target.value); setOpenSug(true); }}
                onFocus={() => setOpenSug(true)}
                onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
              />
              {openSug && suggestions.length > 0 && (
                <div data-testid="hero-search-suggestions" className="absolute z-50 left-0 right-0 bottom-full mb-3 bg-white rounded-2xl shadow-2xl border border-black/5 overflow-hidden">
                  <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-[#4A5D58] font-semibold">Matching Tours</p>
                  {suggestions.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/tour/${p.slug}`}
                      data-testid={`hero-suggestion-${p.slug}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-black/[0.03] transition-colors"
                    >
                      <img src={p.image} alt={p.title} className="w-12 h-12 rounded-lg object-cover" />
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-[#0A221C] truncate">{p.title}</p>
                        <p className="text-xs text-[#4A5D58] truncate">{p.region} · ₹{p.price.toLocaleString("en-IN")}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="hidden md:block">
              <label className="eyebrow text-[#4A5D58] block mb-2">Travel Date</label>
              <input onFocus={(e) => (e.target.type = "date")} type="text" className="ll-input" placeholder="Add dates" id="h-date" />
            </div>
            <div className="hidden md:block">
              <label className="eyebrow text-[#4A5D58] block mb-2">Travellers</label>
              <input className="ll-input" placeholder="No. of guests" id="h-guests" />
            </div>
            <button type="button" onClick={runSearch} data-testid="hero-search-submit" className="btn-primary w-full">
              <Icons.Search size={18} /> Search Tours
            </button>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default function Home() {
  const { data: packages = [] } = usePackages();
  const { data: blogs = [] } = useBlogs();
  const domestic = packages.filter((p) => p.type === "domestic").slice(0, 6);
  const intl = packages.filter((p) => p.type === "international").slice(0, 4);
  const stories = blogs.slice(0, 3);
  const [tIdx, setTIdx] = useState(0);

  return (
    <div>
      <Hero />

      {/* Trust bar + stats */}
      <section className="ll-container pt-24 md:pt-28">
        <Reveal className="flex flex-wrap items-center justify-center gap-6 md:gap-8 pb-14 border-b border-black/5">
          {CERT_LOGOS.map((c) => (
            <div key={c.name} className="bg-white rounded-2xl px-6 py-4 shadow-[0_6px_20px_rgba(10,34,28,0.06)] ring-1 ring-black/5 flex items-center justify-center h-20 md:h-24">
              <img
                src={c.url}
                alt={c.name}
                title={c.name}
                className="h-12 md:h-16 w-auto object-contain"
              />
            </div>
          ))}
          <div className="flex items-center gap-2 text-[#4A5D58] text-sm font-semibold">
            <Icons.ShieldCheck size={18} className="text-[#4ef72d]" /> Govt. Approved Study Tours
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-16">
          {STATS.map((s) => <StatCounter key={s.label} {...s} />)}
        </div>
      </section>

      {/* Our Story */}
      <section className="ll-container ll-section grid md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="relative">
            <ParallaxImage src={IMAGES.planning} alt="Planning your journey with Life Line since 1991" className="rounded-[2rem] w-full h-[520px]" />
            <div className="absolute -bottom-8 -right-4 md:right-8 bg-[#4ef72d] text-[#0A221C] rounded-2xl px-8 py-6 shadow-xl">
              <p className="font-display text-4xl font-bold">35+</p>
              <p className="text-sm font-semibold">Years of Trust</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="eyebrow text-[#f7ad2d]">Our Story</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 leading-tight">A family legacy that began in 1991</h2>
          <p className="text-[#4A5D58] text-lg mt-6 leading-relaxed">
            Life Line Tours and Travels was founded by <strong className="text-[#0A221C]">Mr. Jeevan Hendre</strong> and <strong className="text-[#0A221C]">Mrs. Rekha Hendre</strong> as a humble car rental company in Pune. Over three decades, that promise of reliability grew into a full one-stop travel house.
          </p>
          <p className="text-[#4A5D58] text-lg mt-4 leading-relaxed">
            Today we handle ticketing, passport & visa assistance, car rentals, domestic & international tours, MICE events and government-approved Farmers&apos; Study Tours — all with the same personal, family touch.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/about" className="btn-primary">Read Our Journey <Icons.ArrowRight size={18} /></Link>
            <a href={`tel:${COMPANY.phones[0].replace(/\s/g, "")}`} className="btn-outline"><Icons.Phone size={16} /> Talk to Us</a>
          </div>
        </Reveal>
      </section>

      {/* Featured Domestic */}
      <section className="ll-container ll-section pt-0">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="eyebrow text-[#f7ad2d]">Incredible India</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Featured Domestic Escapes</h2>
          </div>
          <Link to="/domestic-tours" className="btn-outline shrink-0">View All Domestic <Icons.ArrowRight size={18} /></Link>
        </Reveal>
        <HScroll testid="domestic-scroll">
          {domestic.map((p) => (
            <div key={p.slug} className="snap-start shrink-0 w-[85%] sm:w-[380px]">
              <PackageCard pkg={p} />
            </div>
          ))}
        </HScroll>
      </section>

      {/* Why choose us */}
      <section className="bg-[#0A221C] relative overflow-hidden">
        <div className="grain" />
        <div className="ll-container ll-section relative">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <p className="eyebrow text-[#4ef72d]">Why Choose Us</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-3">The Life Line difference</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {WHY_US.map((w) => (
              <StaggerItem key={w.title}>
                <div className="border border-white/10 rounded-3xl p-8 hover:border-[#4ef72d]/50 transition-colors h-full">
                  <div className="w-14 h-14 rounded-2xl bg-[#4ef72d]/15 flex items-center justify-center mb-5">
                    <Icon name={w.icon} size={26} className="text-[#4ef72d]" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">{w.title}</h3>
                  <p className="text-white/65 mt-3 leading-relaxed">{w.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Featured International */}
      <section className="ll-container ll-section">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="eyebrow text-[#f7ad2d]">Around the World</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">International Journeys</h2>
          </div>
          <Link to="/international-tours" className="btn-outline shrink-0">View All International <Icons.ArrowRight size={18} /></Link>
        </Reveal>
        <HScroll testid="international-scroll">
          {intl.map((p) => (
            <div key={p.slug} className="snap-start shrink-0 w-[85%] sm:w-[380px]">
              <PackageCard pkg={p} />
            </div>
          ))}
        </HScroll>
      </section>

      {/* Services */}
      <section className="bg-[#13382F]/5">
        <div className="ll-container ll-section">
          <Reveal className="text-center max-w-2xl mx-auto mb-16">
            <p className="eyebrow text-[#f7ad2d]">One-Stop Travel Solution</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Everything under one roof</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <StaggerItem key={s.title}>
                <div data-testid={`service-${s.title.toLowerCase().replace(/[^a-z]/g, "-")}`} className="ll-card p-8 h-full">
                  <div className="w-14 h-14 rounded-2xl bg-[#0A221C] flex items-center justify-center mb-5">
                    <Icon name={s.icon} size={24} className="text-[#4ef72d]" />
                  </div>
                  <h3 className="font-display text-2xl font-bold">{s.title}</h3>
                  <p className="text-[#4A5D58] mt-3 leading-relaxed">{s.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Testimonials */}
      <section className="ll-container ll-section">
        <Reveal className="text-center mb-10">
          <p className="eyebrow text-[#f7ad2d]">Traveller Stories</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Loved by families for 35 years</h2>
          <a href={REVIEWS_SUMMARY.url} target="_blank" rel="noreferrer" data-testid="reviews-badge" className="inline-flex items-center gap-3 mt-6 bg-white rounded-full pl-4 pr-5 py-2.5 shadow-md ring-1 ring-black/5 hover:shadow-lg transition-shadow">
            <span className="font-display text-2xl font-bold text-[#0A221C]">{REVIEWS_SUMMARY.rating}</span>
            <span className="flex gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => <Icons.Star key={i} size={16} className="text-[#f7ad2d] fill-[#f7ad2d]" />)}
            </span>
            <span className="text-sm text-[#4A5D58] font-medium">{REVIEWS_SUMMARY.count}+ {REVIEWS_SUMMARY.source}</span>
          </a>
        </Reveal>
        <Reveal className="max-w-3xl mx-auto">
          <div data-testid="testimonials" className="ll-card p-10 md:p-14 text-center relative">
            <div className="flex justify-center gap-1 mb-5">
              {Array.from({ length: TESTIMONIALS[tIdx].rating || 5 }).map((_, i) => <Icons.Star key={i} size={18} className="text-[#f7ad2d] fill-[#f7ad2d]" />)}
            </div>
            <motion.p key={tIdx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="font-display text-2xl md:text-3xl leading-snug text-[#0A221C]">
              &ldquo;{TESTIMONIALS[tIdx].quote}&rdquo;
            </motion.p>
            <p className="mt-8 font-semibold text-[#0A221C]">{TESTIMONIALS[tIdx].name}</p>
            <p className="text-sm text-[#4A5D58]">{TESTIMONIALS[tIdx].trip}</p>
            <div className="flex justify-center gap-2 mt-8">
              {TESTIMONIALS.map((_, i) => (
                <button key={i} data-testid={`testimonial-dot-${i}`} onClick={() => setTIdx(i)} className={`h-2.5 rounded-full transition-all ${i === tIdx ? "w-8 bg-[#4ef72d]" : "w-2.5 bg-black/15"}`} aria-label={`Testimonial ${i + 1}`} />
              ))}
            </div>
          </div>
          <div className="text-center mt-8">
            <a href={REVIEWS_SUMMARY.writeUrl} target="_blank" rel="noreferrer" data-testid="write-review-btn" className="btn-outline">
              <Icons.PenLine size={16} /> Write a Google Review
            </a>
          </div>
        </Reveal>
      </section>

      {/* Popular destinations quick links */}
      <section className="ll-container pb-8">
        <Reveal className="flex flex-wrap items-center justify-center gap-3">
          <span className="text-[#4A5D58] font-semibold mr-2">Popular:</span>
          {["Kailash Mansarovar", "Spiti Valley", "Bhutan", "Andaman", "Sri Lanka", "Char Dham"].map((d) => (
            <Link key={d} to="/domestic-tours" className="px-5 py-2.5 rounded-full border border-black/10 text-sm font-medium text-[#0A221C] hover:bg-[#0A221C] hover:text-white transition-colors">
              {d}
            </Link>
          ))}
        </Reveal>
      </section>

      {/* Travel Stories */}
      <section className="ll-container ll-section pb-8">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="eyebrow text-[#f7ad2d]">Travel Stories</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3">Tips & tales from the road</h2>
          </div>
          <Link to="/blog" className="btn-outline shrink-0">Read All Stories <Icons.ArrowRight size={18} /></Link>
        </Reveal>
        <Stagger className="grid md:grid-cols-3 gap-8">
          {stories.map((post) => (
            <StaggerItem key={post.slug}>
              <Link to={`/blog/${post.slug}`} data-testid={`home-blog-${post.slug}`} className="ll-card group block h-full">
                <div className="relative h-52 overflow-hidden">
                  <img src={post.image} alt={post.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out" />
                  <span className="absolute top-4 left-4 badge-dark">{post.category}</span>
                </div>
                <div className="p-6">
                  <p className="text-xs text-[#4A5D58]">{new Date(post.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} · {post.read_time}</p>
                  <h3 className="font-display text-xl font-bold text-[#0A221C] mt-2 leading-snug group-hover:text-[#4ef72d] transition-colors">{post.title}</h3>
                  <p className="text-[#4A5D58] text-sm mt-3 line-clamp-3">{post.excerpt}</p>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <div className="ll-section pt-8">
        <CTABand />
      </div>

      {/* Home enquiry */}
      <section className="ll-container pb-24 grid md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="eyebrow text-[#f7ad2d]">Start Planning</p>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 leading-tight">Let&apos;s craft your next memory</h2>
          <p className="text-[#4A5D58] text-lg mt-5">Share a few details and our travel expert will call you back with a tailor-made itinerary and honest pricing.</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="ll-card p-8"><EnquiryForm /></div>
        </Reveal>
      </section>
    </div>
  );
}
